// Tasks deferred helpers split from the legacy mega-file.
import { sql } from "../../base";
import { getTasksSchemaInfo } from "../../schemaCache";
import type { Task } from "../../types";

// We remember the last time we tidied deferred tasks so we do not re-run the same SQL every few seconds.
const CLEANUP_DEBOUNCE_MS = 60 * 1000; // avoid hammering the database more than once per minute per user
const lastCleanupRun = new Map<string, number>();

export const deferredTasks = {
  async deferToTomorrow(taskId: string, userId: string, deferredNotes?: string | null, totalMinutes?: number | null): Promise<Task> {
      try {
        const schema = await getTasksSchemaInfo();
        const normalizedNotes = deferredNotes ?? null;
        const normalizedMinutes = totalMinutes ?? null;

        const runUpdate = async (ownerField: 'user_id' | 'assigned_to', includeNotes: boolean) => {
          if (includeNotes) {
            if (schema.hasTimeSpent && schema.hasTimeSpentMinutes) {
              return ownerField === 'user_id'
                ? sql`
                    UPDATE tasks
                    SET is_live = false,
                        notes = ${normalizedNotes},
                        time_spent = COALESCE(${normalizedMinutes}, time_spent),
                        time_spent_minutes = COALESCE(${normalizedMinutes}, time_spent_minutes),
                        updated_at = now()
                    WHERE id = ${taskId} AND user_id = ${userId}
                    RETURNING *
                  `
                : sql`
                    UPDATE tasks
                    SET is_live = false,
                        notes = ${normalizedNotes},
                        time_spent = COALESCE(${normalizedMinutes}, time_spent),
                        time_spent_minutes = COALESCE(${normalizedMinutes}, time_spent_minutes),
                        updated_at = now()
                    WHERE id = ${taskId} AND assigned_to = ${userId}
                    RETURNING *
                  `;
            }
            if (schema.hasTimeSpent) {
              return ownerField === 'user_id'
                ? sql`
                    UPDATE tasks
                    SET is_live = false,
                        notes = ${normalizedNotes},
                        time_spent = COALESCE(${normalizedMinutes}, time_spent),
                        updated_at = now()
                    WHERE id = ${taskId} AND user_id = ${userId}
                    RETURNING *
                  `
                : sql`
                    UPDATE tasks
                    SET is_live = false,
                        notes = ${normalizedNotes},
                        time_spent = COALESCE(${normalizedMinutes}, time_spent),
                        updated_at = now()
                    WHERE id = ${taskId} AND assigned_to = ${userId}
                    RETURNING *
                  `;
            }
            if (schema.hasTimeSpentMinutes) {
              return ownerField === 'user_id'
                ? sql`
                    UPDATE tasks
                    SET is_live = false,
                        notes = ${normalizedNotes},
                        time_spent_minutes = COALESCE(${normalizedMinutes}, time_spent_minutes),
                        updated_at = now()
                    WHERE id = ${taskId} AND user_id = ${userId}
                    RETURNING *
                  `
                : sql`
                    UPDATE tasks
                    SET is_live = false,
                        notes = ${normalizedNotes},
                        time_spent_minutes = COALESCE(${normalizedMinutes}, time_spent_minutes),
                        updated_at = now()
                    WHERE id = ${taskId} AND assigned_to = ${userId}
                    RETURNING *
                  `;
            }
            return ownerField === 'user_id'
              ? sql`
                  UPDATE tasks
                  SET is_live = false,
                      notes = ${normalizedNotes},
                      updated_at = now()
                  WHERE id = ${taskId} AND user_id = ${userId}
                  RETURNING *
                `
              : sql`
                  UPDATE tasks
                  SET is_live = false,
                      notes = ${normalizedNotes},
                      updated_at = now()
                  WHERE id = ${taskId} AND assigned_to = ${userId}
                  RETURNING *
                `;
          }

          if (schema.hasTimeSpent && schema.hasTimeSpentMinutes) {
            return ownerField === 'user_id'
              ? sql`
                  UPDATE tasks
                  SET is_live = false,
                      time_spent = COALESCE(${normalizedMinutes}, time_spent),
                      time_spent_minutes = COALESCE(${normalizedMinutes}, time_spent_minutes),
                      updated_at = now()
                  WHERE id = ${taskId} AND user_id = ${userId}
                  RETURNING *
                `
              : sql`
                  UPDATE tasks
                  SET is_live = false,
                      time_spent = COALESCE(${normalizedMinutes}, time_spent),
                      time_spent_minutes = COALESCE(${normalizedMinutes}, time_spent_minutes),
                      updated_at = now()
                  WHERE id = ${taskId} AND assigned_to = ${userId}
                  RETURNING *
                `;
          }
          if (schema.hasTimeSpent) {
            return ownerField === 'user_id'
              ? sql`
                  UPDATE tasks
                  SET is_live = false,
                      time_spent = COALESCE(${normalizedMinutes}, time_spent),
                      updated_at = now()
                  WHERE id = ${taskId} AND user_id = ${userId}
                  RETURNING *
                `
              : sql`
                  UPDATE tasks
                  SET is_live = false,
                      time_spent = COALESCE(${normalizedMinutes}, time_spent),
                      updated_at = now()
                  WHERE id = ${taskId} AND assigned_to = ${userId}
                  RETURNING *
                `;
          }
          if (schema.hasTimeSpentMinutes) {
            return ownerField === 'user_id'
              ? sql`
                  UPDATE tasks
                  SET is_live = false,
                      time_spent_minutes = COALESCE(${normalizedMinutes}, time_spent_minutes),
                      updated_at = now()
                  WHERE id = ${taskId} AND user_id = ${userId}
                  RETURNING *
                `
              : sql`
                  UPDATE tasks
                  SET is_live = false,
                      time_spent_minutes = COALESCE(${normalizedMinutes}, time_spent_minutes),
                      updated_at = now()
                  WHERE id = ${taskId} AND assigned_to = ${userId}
                  RETURNING *
                `;
          }
          return ownerField === 'user_id'
            ? sql`
                UPDATE tasks
                SET is_live = false,
                    updated_at = now()
                WHERE id = ${taskId} AND user_id = ${userId}
                RETURNING *
              `
            : sql`
                UPDATE tasks
                SET is_live = false,
                    updated_at = now()
                WHERE id = ${taskId} AND assigned_to = ${userId}
                RETURNING *
              `;
        };

        const primaryResult = await runUpdate('user_id', schema.hasNotes);

        if (primaryResult && primaryResult.length > 0) {
          return primaryResult[0] as Task;
        }

        const fallbackResult = await runUpdate('assigned_to', schema.hasNotes);

        if (!fallbackResult || fallbackResult.length === 0) {
          throw new Error('Task not updated when deferring to tomorrow');
        }

        return fallbackResult[0] as Task;
      } catch (error) {
        console.error('Error deferring task to tomorrow:', error);
        throw error;
      }
    },

  async cleanupDeferredTasks(userId: string): Promise<number> {
      try {
        // Remove the [Deferred to Tomorrow] marker from tasks' notes
        // This should be called at the start of each new day
        const schema = await getTasksSchemaInfo();
        if (!schema.hasNotes) {
          // Older schema without notes column
          return 0;
        }
        // Reactivate any deferred tasks owned by the user OR assigned to them.
        const matchingOwner = sql`
          UPDATE tasks
          SET notes = CASE 
                       WHEN notes = '[Deferred to Tomorrow]' THEN NULL
                       WHEN notes LIKE '%\n[Deferred to Tomorrow]' THEN REPLACE(notes, '\n[Deferred to Tomorrow]', '')
                       WHEN notes LIKE '[Deferred to Tomorrow]\n%' THEN REPLACE(notes, '[Deferred to Tomorrow]\n', '')
                       WHEN notes LIKE '%[Deferred to Tomorrow]%' THEN REPLACE(notes, '[Deferred to Tomorrow]', '')
                       ELSE notes
                      END,
              is_live = true,
              updated_at = NOW()
          WHERE (user_id = ${userId} OR assigned_to = ${userId})
          AND notes LIKE '%[Deferred to Tomorrow]%'
          AND status NOT IN ('Complete', 'Superseded')
          AND updated_at < DATE_TRUNC('day', NOW())
          RETURNING id
        `;

        const result = await matchingOwner;
        
        // Clean up any notes that became empty or just whitespace
        try {
          await sql`
            UPDATE tasks
            SET notes = NULL
            WHERE (user_id = ${userId} OR assigned_to = ${userId})
            AND (notes = '' OR notes IS NOT NULL AND TRIM(notes) = '')
            AND updated_at > NOW() - INTERVAL '1 minute'
          `;
        } catch (e) {
          // Be defensive in dev: if schema differs, skip this cleanup silently
          console.warn('[cleanupDeferredTasks] Optional cleanup skipped:', (e as any)?.message || e);
        }
        
        return result.length;
      } catch (error) {
        console.error('Error cleaning up deferred tasks:', error);
        return 0;
      }
    }
};

// This helper makes sure "bumped" tasks come back once a new day starts, even if the user never opened Focus Mode.
// It quietly runs the cleanup query at most once per minute so regular page loads stay fast.
export async function ensureDeferredTasksAreFresh(userId: string): Promise<number> {
  const now = Date.now();
  const lastRun = lastCleanupRun.get(userId);

  if (lastRun && now - lastRun < CLEANUP_DEBOUNCE_MS) {
    return 0;
  }

  const cleaned = await deferredTasks.cleanupDeferredTasks(userId);
  lastCleanupRun.set(userId, Date.now());
  return cleaned;
}
